import { useEffect, useState } from "react";
import {
    FunctionField,
    NumberField,
    SimpleShowLayout,
    TextField,
    usePermissions,
    useResourceContext,
    useTranslate,
    TabbedShowLayout,
    Tab,
    useLocaleState,
    useGetList,
    useRecordContext,
    useDataProvider,
    useRefresh,
    useNotify,
} from "react-admin";
import { Box, Button, Grid, TextField as MuiTextField } from "@mui/material";
import { queryToAttribute } from "shared/src/utils/decodeQuery";
import { ToolbarWithHistory } from "shared/src/components/ToolbarWithHistory";
import { AccessRightsError } from "shared/src/components/AccessRightsError";
import { SkuChangeLogs } from "./components/SkuChangeLogs";
import { PrintLabels } from "../components/PrintLabels";
import { EliminateForm } from "../components/EliminateForm";
import { Notes } from "./components/notes/Notes";
import { ProductImageManagement } from "../products/components/productImage/ProductImageManagement";
import { RaDateField } from "shared/src/components/fields/Ra/RaDateField";
import { formatDueInspectionTypes } from "shared/src/utils/formatDueInspectionTypes";
import { EliminateButton } from "../components/buttons/EliminateButton";
import { RaShowView } from "shared/src/components/views/RaShowView";
import { DamageReports } from "./components/DamageReports";
import { SkuInspections } from "./components/SkuInspections";
import { MediaManagement } from "../components/media/MediaManagement";
import { RentalsWithFilterReference } from "../components/RentalsWithFilterReference";
import { ConfirmationDialog } from "shared/src/components/dialogs/ConfirmationDialog";
import BuildIcon from "@mui/icons-material/Build";
import { handleHttpError } from "shared/src/utils/errorHandler";

export const SkuShow = (props) => {
    const [eliminate, setEliminate] = useState(false);
    const [printLabels, setPrintLabels] = useState(false);
    const resource = useResourceContext();
    const translate = useTranslate();
    const attribute = queryToAttribute();
    const { isLoading, permissions } = usePermissions();
    const [locale] = useLocaleState();

    if (isLoading) {
        return <></>;
    } else if (!permissions.vote({ resource, attribute })) {
        return <AccessRightsError />;
    }
    return (
        <RaShowView
            marginBottom={3}
            {...props}
            actions={
                <ToolbarWithHistory
                    back={true}
                    edit={true}
                    print={true}
                    setPrintLabels={setPrintLabels}
                    download={true}
                    downloadLabel={translate("equip.downloadEliminationPdf")}
                    downloadLink="elimination_pdf"
                />
            }
        >
            {eliminate && <EliminateForm setEliminate={setEliminate} />}
            {printLabels && <PrintLabels setPrintLabels={setPrintLabels} />}
            <Box
                sx={{
                    flex: 1,
                    pt: "1em"
                }}>
                <Grid container sx={{
                    position: "relative"
                }}>
                    <Grid
                        sx={{
                            m: 6,
                            justifyContent: "flex-start",
                            marginLeft: 0
                        }}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-start"
                            }}>
                            <ProductImageManagement />
                            <SimpleShowLayout>
                                <TextField
                                    label="equip.name"
                                    source={`product.name.${locale}`}
                                    emptyText={translate("equip.notAvailable")}
                                />
                                <TextField
                                    label="equip.description"
                                    source={`product.description.${locale}`}
                                    emptyText={translate("equip.notAvailable")}
                                />
                                <TextField
                                    label="equip.manufacturer"
                                    source="manufacturer.name"
                                    emptyText={translate("equip.notAvailable")}
                                />
                            </SimpleShowLayout>
                        </Box>
                        <Box
                            sx={{
                                marginTop: 3,
                                marginLeft: 7
                            }}>
                            <SimpleShowLayout>
                                <TextField
                                    label="equip.labelNumber"
                                    source="labelNumber"
                                    emptyText={translate("equip.notAvailable")}
                                />
                                <TextField
                                    label="equip.serialNumber"
                                    source="serialNumber"
                                    emptyText={translate("equip.notAvailable")}
                                />
                                <TextField
                                    emptyText={translate("equip.notAvailable")}
                                    label="equip.edispo.id"
                                    source="product.edispoId"
                                />
                                <FunctionField
                                    label="equip.edispo.type"
                                    render={(record) =>
                                        translate(
                                            record.product.edispoType
                                                ? `equip.edispo.${record.product.edispoType}`
                                                : "equip.notAvailable"
                                        )
                                    }
                                />
                                <FunctionField
                                    label="equip.status"
                                    render={(record) =>
                                        translate(`equip.sku_status_${record.status}`)
                                    }
                                />
                                <FunctionField
                                    label="equip.availability"
                                    render={(record) =>
                                        translate(`equip.sku_availability_${record.availability}`)
                                    }
                                />
                                <FunctionField
                                    label="equip.maintenanceAlarm"
                                    render={(record) =>
                                        record.maintenanceAlarm
                                            ? translate("equip.yes")
                                            : translate("equip.no")
                                    }
                                />
                                <FunctionField
                                    label="equip.maintenanceName"
                                    render={(record) =>
                                        record.maintenanceName || translate("equip.notAvailable")
                                    }
                                />
                                <FunctionField
                                    label="equip.maintenanceRentalDays"
                                    render={(record) =>
                                        formatMaintenanceRentalDays(record, translate)
                                    }
                                />
                                <FunctionField
                                    label="equip.maintenanceOverdueDays"
                                    render={(record) =>
                                        record.maintenanceDue
                                            ? `${record.maintenanceOverdueDays} ${translate(
                                                  "equip." +
                                                      (record.maintenanceOverdueDays === 1
                                                          ? "day"
                                                          : "days")
                                              )}`
                                            : translate("equip.notAvailable")
                                    }
                                />
                                <MaintenanceCompleteButton />
                                <FunctionField
                                    label="equip.inspectionRequired"
                                    render={(record) =>
                                        formatDueInspectionTypes(record, translate) ??
                                        translate("equip.no")
                                    }
                                />
                                <RaDateField
                                    label="equip.lastVdeInspectionAt"
                                    source="lastVdeInspectionAt"
                                    emptyText={translate("equip.notAvailable")}
                                />
                                <RaDateField
                                    label="equip.lastUvvInspectionAt"
                                    source="lastUvvInspectionAt"
                                    emptyText={translate("equip.notAvailable")}
                                />
                                <EliminateButton setEliminate={setEliminate} />
                            </SimpleShowLayout>
                        </Box>
                    </Grid>
                    <Grid sx={{
                        m: 6
                    }}>
                        <Box>
                            <SimpleShowLayout>
                                <TextField
                                    label="equip.supplier"
                                    source="supplier.name"
                                    emptyText={translate("equip.notAvailable")}
                                />
                                <TextField
                                    label="equip.storageLocation"
                                    source="storageLocation.name"
                                    emptyText={translate("equip.notAvailable")}
                                />
                                <RaDateField
                                    label="equip.deliveryDate"
                                    source="arrival.deliveryDate"
                                    emptyText={translate("equip.notAvailable")}
                                />
                            </SimpleShowLayout>
                        </Box>
                        <Box sx={{
                            marginTop: 2.8
                        }}>
                            <SimpleShowLayout>
                                <NumberField
                                    label="equip.purchasePrice"
                                    source="arrival.purchasePrice"
                                    options={{
                                        style: "currency",
                                        currency: "EUR",
                                    }}
                                    emptyText={translate("equip.notAvailable")}
                                />
                                <NumberField
                                    label="equip.currentPrice"
                                    source="currentPrice"
                                    options={{
                                        style: "currency",
                                        currency: "EUR",
                                    }}
                                    emptyText={translate("equip.notAvailable")}
                                />
                                <NumberField
                                    label="equip.weight"
                                    source="weight"
                                    options={{
                                        maximumFractionDigits: 2,
                                        minimumFractionDigits: 2,
                                        style: "unit",
                                        unit: "kilogram",
                                    }}
                                    emptyText={translate("equip.notAvailable")}
                                />
                            </SimpleShowLayout>
                        </Box>
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid size={{ xs: 12 }}>
                        <SimpleShowLayout>
                            <SkuShowTabs />
                        </SimpleShowLayout>
                    </Grid>
                </Grid>
            </Box>
        </RaShowView>
    );
};

const formatMaintenanceRentalDays = (record, translate) => {
    if (record.maintenanceRentalDays == null) {
        return translate("equip.notAvailable");
    }

    return record.maintenanceAlarm
        ? `${record.maintenanceRentalDaysCount ?? 0} / ${record.maintenanceRentalDays}`
        : record.maintenanceRentalDays;
};

const MaintenanceCompleteButton = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const refresh = useRefresh();
    const notify = useNotify();
    const [open, setOpen] = useState(false);
    const [maintenanceRentalDays, setMaintenanceRentalDays] = useState(
        record?.maintenanceRentalDays ?? ""
    );

    if (!record?.maintenanceDue) {
        return null;
    }

    const completeMaintenance = () => {
        dataProvider
            .performAction("skus", {
                id: record.id,
                actionName: "completeMaintenance",
                data: {
                    maintenanceRentalDays:
                        maintenanceRentalDays === ""
                            ? null
                            : Number(maintenanceRentalDays),
                },
            })
            .then(() => {
                setOpen(false);
                refresh();
            })
            .catch((e) => handleHttpError(e, notify));
    };

    return (
        <>
            <Button
                startIcon={<BuildIcon />}
                onClick={() => setOpen(true)}
                title={translate("equip.completeMaintenance")}
            >
                {translate("equip.completeMaintenance")}
            </Button>
            {open && (
                <ConfirmationDialog
                    label={translate("equip.completeMaintenance")}
                    onConfirm={completeMaintenance}
                    onCancel={() => setOpen(false)}
                    content={
                        <Box sx={{ pt: 1 }}>
                            <MuiTextField
                                fullWidth
                                type="number"
                                label={translate("equip.nextMaintenanceRentalDays")}
                                value={maintenanceRentalDays}
                                onChange={(event) =>
                                    setMaintenanceRentalDays(event.target.value)
                                }
                            />
                        </Box>
                    }
                />
            )}
        </>
    );
};

const SkuShowTabs = () => {
    const translate = useTranslate();
    const { isLoading: isPermissionsLoading, permissions } = usePermissions();
    const record = useRecordContext();
    const [lastRentalItem, setLastRentalItem] = useState(null);
    const canReadRentalItems =
        !isPermissionsLoading &&
        permissions.vote({ resource: "rentalItems", attribute: "read" });
    const { data: rentalItems, isLoading } = useGetList("rentalItems", {
        filter: {
            sku: record.id,
            _embed: "rental",
        },
        pagination: {
            page: 1,
            perPage: 1,
        },
        sort: { field: "id", order: "DESC" },
    }, { enabled: canReadRentalItems && !!record?.id });

    useEffect(() => {
        if (rentalItems && rentalItems.length > 0) {
            setLastRentalItem(rentalItems[0]);
        }
    }, [rentalItems]);

    if (isPermissionsLoading || (canReadRentalItems && isLoading)) {
        return <></>;
    }

    return (
        <TabbedShowLayout sx={{ mb: 5 }}>
            {permissions.vote({
                resource: "changeLogs",
                attribute: "read",
            }) && (
                <Tab label="equip.changeLogs" title={translate("equip.changeLogs")}>
                    {<SkuChangeLogs />}
                </Tab>
            )}

            <Tab label="equip.media" title={translate("equip.media")}>
                <MediaManagement resourceFilterKey="sku" />
            </Tab>

            {permissions.vote({ resource: "rentals", attribute: "read" }) && (
                <Tab label="equip.rentals" title={translate("equip.rentals")}>
                    <RentalsWithFilterReference target="sku" />
                </Tab>
            )}

            {permissions.vote({ resource: "skuNotes", attribute: "read" }) && (
                <Tab label="equip.comments" title={translate("equip.comments")}>
                    <Notes />
                </Tab>
            )}

            {permissions.vote({ resource: "skuInspections", attribute: "read" }) && (
                <Tab label="equip.skuInspections" title={translate("equip.skuInspections")}>
                    <SkuInspections />
                </Tab>
            )}

            {permissions.vote({ resource: "damageReports", attribute: "read" }) && (
                <Tab label="equip.damageReports" title={translate("equip.damageReports")}>
                    <DamageReports lastRentalItem={lastRentalItem} />
                </Tab>
            )}
        </TabbedShowLayout>
    );
};
